'use client';
import React, { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Link from 'next/link';
import { getTranslations } from '@/lib/i18n';
import { useLanguage } from '@/lib/LanguageContext';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSection {
  id: string;
  title: string;
  color: string;
  items: FAQItem[];
}

const AccordionItem: React.FC<{
  item: FAQItem;
  isOpen: boolean;
  onToggle: () => void;
  accentColor: string;
}> = ({ item, isOpen, onToggle, accentColor }) => {
  return (
    <div
      className="border-b border-gray-100 last:border-b-0"
      style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
      <button
        onClick={onToggle}
        className="w-full text-left flex items-start justify-between gap-4 py-5 px-0 group"
        aria-expanded={isOpen}>
        <span
          className="font-semibold text-[15px] leading-snug transition-colors duration-200"
          style={{
            fontFamily: "'Nunito Sans', sans-serif",
            color: isOpen ? accentColor : '#111',
          }}>
          {item.question}
        </span>
        <span
          className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full border transition-all duration-300 mt-0.5"
          style={{
            borderColor: isOpen ? accentColor : '#ccc',
            backgroundColor: isOpen ? accentColor : 'transparent',
            transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
          }}>
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
              d="M5 1V9M1 5H9"
              stroke={isOpen ? '#000' : '#666'}
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </span>
      </button>
      <div
        className="overflow-hidden transition-all duration-300"
        style={{ maxHeight: isOpen ? '400px' : '0px', opacity: isOpen ? 1 : 0 }}>
        <p
          className="text-gray-600 text-[14px] leading-relaxed pb-5"
          style={{ fontFamily: "'Nunito Sans', sans-serif" }}>
          {item.answer}
        </p>
      </div>
    </div>
  );
};

export default function FAQPage() {
  const { language } = useLanguage();
  const t = getTranslations(language);

  const faqSections: FAQSection[] = t.faq.sections.map((section) => ({
    id: section.id,
    title: section.title,
    color: section.color,
    items: section.items,
  }));

  const [openItems, setOpenItems] = useState<Record<string, number | null>>({
    servicios: 0,
    precios: null,
    soporte: null,
  });

  const toggle = (sectionId: string, index: number) => {
    setOpenItems((prev) => ({
      ...prev,
      [sectionId]: prev[sectionId] === index ? null : index,
    }));
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Raleway', arial, sans-serif" }}>
      <Navbar />

      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{ paddingTop: '140px', paddingBottom: '80px', backgroundColor: '#000' }}>
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 50%, #1AEF0F 0%, transparent 50%), radial-gradient(circle at 80% 20%, #1bb0ce 0%, transparent 40%)',
          }}
        />
        <div className="max-w-[1200px] mx-auto px-[35px] relative z-10">
          <div className="flex items-center gap-2 mb-4">
            <Link
              href="/"
              className="text-gray-400 text-sm hover:text-white transition-colors"
              style={{ fontFamily: "'Nunito Sans', sans-serif" }}>
              {t.faq.breadcrumbHome}
            </Link>
            <span className="text-gray-600 text-sm">/</span>
            <span
              className="text-sm"
              style={{ color: '#1AEF0F', fontFamily: "'Nunito Sans', sans-serif" }}>
              {t.faq.breadcrumbFaq}
            </span>
          </div>
          <h1
            className="text-white font-bold mb-4"
            style={{
              fontSize: 'clamp(32px, 5vw, 56px)',
              lineHeight: '1.1',
              fontFamily: "'Titillium Web', sans-serif",
            }}>
            {t.faq.heroTitle1}{' '}
            <span style={{ color: '#1AEF0F' }}>{t.faq.heroTitle2}</span>
          </h1>
          <p
            className="text-gray-300 max-w-[560px]"
            style={{ fontSize: '16px', lineHeight: '1.7', fontFamily: "'Nunito Sans', sans-serif" }}>
            {t.faq.heroSubtitle}
          </p>
        </div>
      </section>

      {/* Section Nav Pills */}
      <div className="sticky top-[72px] z-40 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-[1200px] mx-auto px-[35px]">
          <div className="flex gap-1 overflow-x-auto py-3 scrollbar-hide">
            {faqSections.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className="flex-shrink-0 px-4 py-2 text-sm font-semibold border transition-all duration-200 hover:border-black"
                style={{
                  fontFamily: "'Nunito Sans', sans-serif",
                  borderColor: '#e5e7eb',
                  color: '#333',
                }}>
                {section.title.split(' ').slice(-2).join(' ')}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ Sections */}
      <main className="max-w-[1200px] mx-auto px-[35px] py-16">
        <div className="flex flex-col gap-16">
          {faqSections.map((section) => (
            <section key={section.id} id={section.id} className="scroll-mt-32">
              {/* Section Header */}
              <div className="flex items-center gap-4 mb-8">
                <div
                  className="w-1 h-10 flex-shrink-0"
                  style={{ backgroundColor: section.color }}
                />
                <h2
                  className="font-bold text-black"
                  style={{
                    fontSize: 'clamp(20px, 2.5vw, 28px)',
                    fontFamily: "'Titillium Web', sans-serif",
                  }}>
                  {section.title}
                </h2>
              </div>

              {/* Two-column accordion layout on desktop */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-16">
                <div className="border-t" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
                  {section.items.slice(0, Math.ceil(section.items.length / 2)).map((item, i) => (
                    <AccordionItem
                      key={i}
                      item={item}
                      isOpen={openItems[section.id] === i}
                      onToggle={() => toggle(section.id, i)}
                      accentColor={section.color}
                    />
                  ))}
                </div>
                <div className="border-t" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
                  {section.items
                    .slice(Math.ceil(section.items.length / 2))
                    .map((item, i) => {
                      const realIndex = Math.ceil(section.items.length / 2) + i;
                      return (
                        <AccordionItem
                          key={realIndex}
                          item={item}
                          isOpen={openItems[section.id] === realIndex}
                          onToggle={() => toggle(section.id, realIndex)}
                          accentColor={section.color}
                        />
                      );
                    })}
                </div>
              </div>
            </section>
          ))}
        </div>

        {/* Still have questions CTA */}
        <div
          className="mt-20 relative overflow-hidden"
          style={{ backgroundColor: '#000', padding: '48px 40px' }}>
          <div
            className="absolute inset-0 opacity-15"
            style={{
              backgroundImage:
                'radial-gradient(circle at 90% 50%, #1AEF0F 0%, transparent 50%)',
            }}
          />
          <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <h3
                className="text-white font-bold mb-2"
                style={{
                  fontSize: 'clamp(18px, 2vw, 24px)',
                  fontFamily: "'Titillium Web', sans-serif",
                }}>
                {t.faq.ctaTitle}
              </h3>
              <p
                className="text-gray-400 text-sm"
                style={{ fontFamily: "'Nunito Sans', sans-serif" }}>
                {t.faq.ctaSubtitle}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="/contact"
                className="group relative inline-flex items-center overflow-hidden border border-[#1AEF0F] text-[#1AEF0F] bg-transparent flex-shrink-0"
                style={{
                  padding: '12px 28px',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 300ms ease-out',
                  fontFamily: "'Nunito Sans', sans-serif",
                }}>
                <span className="relative z-10 transition-colors duration-300 group-hover:text-black">
                  {t.faq.ctaContact}
                </span>
                <span
                  className="absolute inset-0 bg-[#1AEF0F] transform translate-x-full group-hover:translate-x-0 transition-transform duration-300"
                  style={{ zIndex: 0 }}
                />
              </a>
              <a
                href="https://wa.me/56991001317"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative inline-flex items-center overflow-hidden border border-white text-white bg-transparent flex-shrink-0"
                style={{
                  padding: '12px 28px',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 300ms ease-out',
                  fontFamily: "'Nunito Sans', sans-serif",
                }}>
                <span className="relative z-10 transition-colors duration-300 group-hover:text-black">
                  {t.faq.ctaWhatsapp}
                </span>
                <span
                  className="absolute inset-0 bg-white transform translate-x-full group-hover:translate-x-0 transition-transform duration-300"
                  style={{ zIndex: 0 }}
                />
              </a>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
